import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CasosService } from '../casos/casos.service';
import { getCurrentLanguage } from '../core/helpers/utils';


interface Parte {
  id: string;
  razon_social: string;
}

interface Participante {
  arbitro_rol: string;
  usuario: {
    nombre: string;
    apellidos: string;
  };
}



@Component({
  selector: 'app-tabla-financiera',
  templateUrl: './tabla-financiera.component.html',
  styleUrls: ['./tabla-financiera.component.scss']
})
export class TablaFinancieraComponent implements OnInit {

  @Output() refreshCase = new EventEmitter<void>();
  @Input() caso: any;

  loaded: boolean = false;
  error: boolean = false;
  // Agregar al componente

  combinedHonorariosRows = ['Neto', 'IVA', 'Total', 'Neto', 'IVA', 'Total']; // Número de filas combinado

  constructor(
    private casosService: CasosService,
    private route: ActivatedRoute
  ) { }

  // Secciones para Provisión de Fondos
  sections = [
    'Derechos de admisión',
    'Honorarios de los árbitros',
    'Derechos de administración',
    'Gastos del expediente',
    'Gastos de los árbitros',
    'Alquiler de salas'
  ];

  // Secciones para Liquidación, excluye "Honorarios de los árbitros", "Gastos del expediente" y "Derechos de administración", y añade "Honorarios, Derechos y Gastos"
  liquidationSections = [
    'Derechos de admisión',
    'Honorarios, derechos y gastos', // Nueva sección fusionada
    'Gastos de los árbitros', // Nueva sección añadida
    'Alquiler de salas'
  ];

  ngOnInit(): void {
    const casoId = this.route.snapshot.paramMap.get('id');
    if (casoId) {
      this.casosService.getCaso(casoId).subscribe({
        next: (data) => {
          this.caso = data;

          // Verificar si "financiero" es un objeto vacío o no
          if (this.caso.financiero && Object.keys(this.caso.financiero).length > 0) {
            this.loaded = true;
          } else {
            this.error = true;
            this.loaded = true;
          }
        },
        error: (err) => {
          this.error = true;
          this.loaded = true;
          console.error('Error al cargar el caso:', err);
        }
      });
    } else {
      this.error = true;
      this.loaded = true;
    }
  }



  isLiquidationSection(section: string): boolean {
    return section === 'Honorarios de los árbitros' || section === 'Derechos de administración';
  }

  getLiquidationSectionHeader(section: string): string {
    // Cambiar el título para la sección fusionada
    if (this.isLiquidationSection(section)) {
      return 'Honorarios, derechos y gastos';
    }
    return section;
  }


  getParteNombre(parteId: string): string {
    if (this.caso && this.caso.partes) {
      const parte = this.caso.partes.find((parte: Parte) => parte.id === parteId);
      return parte ? parte.razon_social : 'Parte Desconocida';
    }
    return 'Parte Desconocida';
  }

  // Métodos para "demanda"
  getTotal(section: string, row: string, partes: any[]): number {
    return partes.reduce((total, parte) => total + (parte[section.toLowerCase().replace(/\s+/g, '') + row] || 0), 0);
  }

  get totalColspan(): number {
    return this.demandantesColspan + this.demandadosColspan;
  }

  get filteredDemandantes(): any[] {
    return this.caso.financiero.demanda.provisionFondos.demandantes.slice(0, -1);
  }

  get filteredDemandados(): any[] {
    return this.caso.financiero.demanda.provisionFondos.demandados.slice(0, -1);
  }

  get demandantesTotal(): any {
    return this.caso.financiero.demanda.provisionFondos.demandantes[this.caso.financiero.demanda.provisionFondos.demandantes.length - 1];
  }

  get demandadosTotal(): any {
    return this.caso.financiero.demanda.provisionFondos.demandados[this.caso.financiero.demanda.provisionFondos.demandados.length - 1];
  }

  get demandantesColspan(): number {
    return this.filteredDemandantes.length + (this.filteredDemandantes.length > 1 ? 1 : 0);
  }

  get demandadosColspan(): number {
    return this.filteredDemandados.length + (this.filteredDemandados.length > 1 ? 1 : 0);
  }

  get filteredLiquidacionDemandantes(): any[] {
    return this.caso.financiero.demanda.liquidacion.demandantes.slice(0, -1);
  }

  get filteredLiquidacionDemandados(): any[] {
    return this.caso.financiero.demanda.liquidacion.demandados.slice(0, -1);
  }

  get liquidacionDemandantesTotal(): any {
    return this.caso.financiero.demanda.liquidacion.demandantes[this.caso.financiero.demanda.liquidacion.demandantes.length - 1];
  }

  get liquidacionDemandadosTotal(): any {
    return this.caso.financiero.demanda.liquidacion.demandados[this.caso.financiero.demanda.liquidacion.demandados.length - 1];
  }

  getFormattedValue(value: number): string {
    if (value !== null && value !== undefined) {
      return value.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    } else {
      return '';
    }
  }

  formatPercentage(value: number | string | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'number') {
      return value.toLocaleString('es-ES', { useGrouping: false, maximumFractionDigits: 20 });
    }

    return String(value).replace(/\./g, ',');
  }

  getTotalValue(totalObject: any, section: string, row: string): string {
    if (!totalObject) {
      return '-';
    }

    const propertyMap: { [key: string]: string } = {
      'Derechos de admisión': 'derechosAdmision',
      'Gastos de los árbitros': 'gastosArbitros', // Añadir la nueva sección
      'Honorarios de los árbitros': 'honorariosArbitros',
      'Derechos de administración': 'derechosAdministracionCentro',
      'Honorarios, derechos y gastos': 'honorarioDerechoGastoExpediente', // Mapeo para la nueva sección de liquidación
      'Gastos del expediente': 'gastosArbitraje',
      'Alquiler de salas': 'alquilerSalas'
    };

    // Manejar el caso específico de "Gastos de los Árbitros" con solo la fila "Total"
    const propertyPrefix = propertyMap[section];
    const propertyKey = section === 'Gastos de los Árbitros' && row === 'Total' ? 'gastosArbitrosTotal' : propertyPrefix + row;

    /*
    if (section==="Honorarios y derechos")
      console.log("Honorarios y derechos", propertyMap )
    */

    if (propertyKey && totalObject[propertyKey] !== undefined) {
      const value = parseFloat(totalObject[propertyKey]);
      return this.getFormattedValue(value);
    }

    return '';
  }


  getValue(d: any, section: string, row: string): string {
    const propertyMap: { [key: string]: string } = {
      'Derechos de admisión': 'derechosAdmision',
      'Gastos de los árbitros': 'gastosArbitros', // Añadir la nueva sección
      'Honorarios de los árbitros': 'honorariosArbitros',
      'Derechos de administración': 'derechosAdministracionCentro',
      'Honorarios, derechos y gastos': 'honorarioDerechoGastoExpediente', // Mapeo para la nueva sección de liquidación
      'Gastos del expediente': 'gastosArbitraje',
      'Alquiler de salas': 'alquilerSalas'
    };

    const propertyPrefix = propertyMap[section];

    // Manejar el caso específico de "Gastos de los Árbitros" con solo la fila "Total"
    const propertyKey = section === 'Gastos de los Árbitros' && row === 'Total' ? 'gastosArbitrosTotal' : propertyPrefix + row;

    if (propertyKey && d[propertyKey] !== undefined) {
      let value = parseFloat(d[propertyKey]);
      value = Math.abs(value) < 0.005 ? 0.00 : value; //Redondeo
      return this.getFormattedValue(value);
    }

    return '';
  }


  getLiquidacionValue(d: any, section: string, row: string): string {
    const propertyMap: { [key: string]: string } = {
      'Derechos de admisión': 'derechosAdmision',
      'Gastos de los árbitros': 'gastosArbitros', // Añadir la nueva sección
      'Honorarios, derechos y gastos': 'honorarioDerechoGastoExpediente', // Nueva sección mapeada para liquidación
      'Alquiler de salas': 'alquilerSalas'
    };

    const propertyPrefix = propertyMap[section];
    const propertyKey = propertyPrefix ? propertyPrefix + row : '';

    if (propertyKey && d[propertyKey] !== undefined) {
      let value = parseFloat(d[propertyKey]);
      value = Math.abs(value) < 0.005 ? 0.00 : value; //Redondeo
      return this.getFormattedValue(value);
    }

    return '';
  }

  getLiquidacionTotalValue(totalObject: any, section: string, row: string): string {
    if (!totalObject) {
      return '-';
    }

    const propertyMap: { [key: string]: string } = {
      'Derechos de admisión': 'derechosAdmision',
      'Gastos de los árbitros': 'gastosArbitros', // Añadir la nueva sección
      'Honorarios de los árbitros': 'honorariosArbitros',
      'Derechos de administración': 'derechosAdministracionCentro',
      'Honorarios, derechos y gastos': 'honorarioDerechoGastoExpediente', // Mapeo para la nueva sección de liquidación
      'Alquiler de salas': 'alquilerSalas'
    };

    // Manejar el caso específico de "Gastos de los Árbitros" con solo la fila "Total"
    const propertyPrefix = propertyMap[section];
    const propertyKey = section === 'Gastos de los Árbitros' && row === 'Total' ? 'gastosArbitrosTotal' : propertyPrefix + row;

    /*
    if (section==="Honorarios y derechos")
      console.log("Honorarios y derechos", propertyMap )
    */

    if (propertyKey && totalObject[propertyKey] !== undefined) {
      const value = parseFloat(totalObject[propertyKey]);
      return this.getFormattedValue(value);
    }

    return '';
  }


  // Métodos para "reconvencion"
  shouldShowReconvencion(): boolean {
    const cuantiaReconvencion = this.caso?.cuantia_reconvencion;
    return cuantiaReconvencion !== null && cuantiaReconvencion > 0;
  }


  get totalColspanReconvencion(): number {
    return this.reconvencionDemandantesColspan + this.reconvencionDemandadosColspan;
  }

  get filteredReconvencionDemandantes(): any[] {
    return this.caso.financiero.reconvencion.provisionFondos.demandantes.slice(0, -1);
  }

  get filteredReconvencionDemandados(): any[] {
    return this.caso.financiero.reconvencion.provisionFondos.demandados.slice(0, -1);
  }

  get reconvencionDemandantesTotal(): any {
    return this.caso.financiero.reconvencion.provisionFondos.demandantes[this.caso.financiero.reconvencion.provisionFondos.demandantes.length - 1];
  }

  get reconvencionDemandadosTotal(): any {
    return this.caso.financiero.reconvencion.provisionFondos.demandados[this.caso.financiero.reconvencion.provisionFondos.demandados.length - 1];
  }

  get reconvencionDemandantesColspan(): number {
    return this.filteredReconvencionDemandantes.length + (this.filteredReconvencionDemandantes.length > 1 ? 1 : 0);
  }

  get reconvencionDemandadosColspan(): number {
    return this.filteredReconvencionDemandados.length + (this.filteredReconvencionDemandados.length > 1 ? 1 : 0);
  }

  get filteredReconvencionLiquidacionDemandantes(): any[] {
    return this.caso.financiero.reconvencion.liquidacion.demandantes.slice(0, -1);
  }

  get filteredReconvencionLiquidacionDemandados(): any[] {    
    return this.caso.financiero.reconvencion.liquidacion.demandados.slice(0, -1);
  }

  get reconvencionLiquidacionDemandantesTotal(): any {
    return this.caso.financiero.reconvencion.liquidacion.demandantes[this.caso.financiero.reconvencion.liquidacion.demandantes.length - 1];
  }

  get reconvencionLiquidacionDemandadosTotal(): any {
    return this.caso.financiero.reconvencion.liquidacion.demandados[this.caso.financiero.reconvencion.liquidacion.demandados.length - 1];
  }

  getTotalReconvencionValue(totalObject: any, section: string, row: string): string {
    if (!totalObject) {
      return '-';
    }

    const propertyMap: { [key: string]: string } = {
      'Derechos de admisión': 'derechosAdmision',
      'Gastos de los árbitros': 'gastosArbitros', // Añadir la nueva sección
      'Honorarios de los árbitros': 'honorariosArbitros',
      'Derechos de administración': 'derechosAdministracionCentro',
      'Honorarios, derechos y gastos': 'honorarioDerechoGastoExpediente', // Mapeo para la nueva sección de liquidación
      'Gastos del expediente': 'gastosArbitraje',
      'Alquiler de salas': 'alquilerSalas'
    };

    const propertyPrefix = propertyMap[section];
    const propertyKey = propertyPrefix ? propertyPrefix + row : '';

    if (propertyKey && totalObject[propertyKey] !== undefined) {
      const value = parseFloat(totalObject[propertyKey]);
      return this.getFormattedValue(value);
    }

    return '';
  }

  getReconvencionValue(d: any, section: string, row: string): string {
    const propertyMap: { [key: string]: string } = {
      'Derechos de admisión': 'derechosAdmision',
      'Gastos de los árbitros': 'gastosArbitros', // Añadir la nueva sección
      'Honorarios de los árbitros': 'honorariosArbitros',
      'Derechos de administración': 'derechosAdministracionCentro',
      'Honorarios, derechos y gastos': 'honorarioDerechoGastoExpediente', // Mapeo para la nueva sección de liquidación
      'Gastos del expediente': 'gastosArbitraje',
      'Alquiler de salas': 'alquilerSalas'
    };

    const propertyPrefix = propertyMap[section];
    const propertyKey = propertyPrefix ? propertyPrefix + row : '';

    if (propertyKey && d[propertyKey] !== undefined) {
      let value = parseFloat(d[propertyKey]);
      value = Math.abs(value) < 0.005 ? 0.00 : value; //Redondeo
      return this.getFormattedValue(value);
    }

    return '';
  }

  getReconvencionLiquidacionValue(d: any, section: string, row: string): string {
    const propertyMap: { [key: string]: string } = {
      'Derechos de admisión': 'derechosAdmision',
      'Gastos de los árbitros': 'gastosArbitros', // Añadir la nueva sección
      'Honorarios, derechos y gastos': 'honorarioDerechoGastoExpediente', // Nueva sección mapeada para liquidación
      'Gastos del expediente': 'gastosArbitraje',
      'Alquiler de salas': 'alquilerSalas'
    };

    const propertyPrefix = propertyMap[section];
    const propertyKey = propertyPrefix ? propertyPrefix + row : '';

    if (propertyKey && d[propertyKey] !== undefined) {
      let value = parseFloat(d[propertyKey]);
      value = Math.abs(value) < 0.005 ? 0.00 : value; //Redondeo
      return this.getFormattedValue(value);
    }

    return '';
  }

  addLiquidado(value: string) {
    if (value === "0,00 €")
      return "(Liquidado) " + value
    else return value;
  }

  getArbitroRolNombre(rol: string): string {
    const participante = this.caso?.participantes?.find((p: Participante) => p.arbitro_rol === rol);
    return participante ? `(${participante.usuario.nombre} ${participante.usuario.apellidos})` : '';
  }

  translate(term: string): string {
    const translations: { [key: string]: { en: string } } = {
      // Traducciones para combinedHonorariosRows
      'Neto': { en: 'Net' },
      'IVA': { en: 'VAT' },
      'Total': { en: 'Total' },

      // Traducciones para sections
      'Derechos de admisión': { en: 'Admission Fees' },
      'Honorarios de los árbitros': { en: 'Arbitrators’ Fees' },
      'Derechos de administración': { en: 'Administrative Fees' },
      'Gastos del expediente': { en: 'Case Expenses' },
      'Gastos de los árbitros': { en: 'Arbitrators’ Expenses' },
      'Alquiler de salas': { en: 'Room Rentals' },

      // Traducciones para liquidationSections
      'Honorarios, derechos y gastos': { en: 'Fees, Rights, and Expenses' }
    };

    let currentLanguage: string = getCurrentLanguage();

    // Si el idioma actual es "en", buscar la traducción
    if (currentLanguage === 'en' && translations[term]) {
      return translations[term].en || term;
    }

    // Si no hay traducción o el idioma no es "en", devolver el término original
    return term;
}



}
